// @ts-nocheck
import React, { useMemo, useState, ChangeEvent, FormEvent } from "react";
import styled, { css, ThemeProps, withTheme } from "styled-components";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextArea, { TextAreaPresentationProps } from "@insite/mobius/TextArea";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { connect, ResolveThunks } from "react-redux";
import CustomerInformationFields, {
    CustomerFormErrors,
    CustomerFormState,
    emptyCustomerFormState,
    toCustomerInformationModel,
} from "@store/Library/Components/Forms/CustomerInformationFields";
import FormActions, { FormActionsProps } from "@store/Library/Components/Toolkit/FormActions";
import FormWrapper from "@store/Library/Components/Toolkit/FormWrapper";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import LoadingOverlay, { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import submitForm from "@store/Framework/Store/Store/Pages/Crm/Handlers/SubmitCrmForm";
import { FormFieldModel } from "@store/Framework/Types/ApiModels";
import { configurationFormProps } from "@store/Themes/common/props/configuratorForm";

const enum fields {
    title = "title",
    introText = "introText",
    questionnaireText = "questionnaireText",
    questionnaireUrl = "questionnaireUrl",
    seriesOptions = "seriesOptions",
    sizeOptions = "sizeOptions",
    flangeOptions = "flangeOptions",
    gateOptions = "gateOptions",
    sleeveOptions = "sleeveOptions",
    actuatorOptions = "actuatorOptions",
}

const enum optionFields {
    value = "value",
    label = "label",
}

export interface OptionItem {
    fields: {
        [optionFields.value]: string;
        [optionFields.label]?: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.introText]: string;
        [fields.questionnaireText]: string;
        [fields.questionnaireUrl]: string;
        [fields.seriesOptions]: OptionItem[];
        [fields.sizeOptions]: OptionItem[];
        [fields.flangeOptions]: OptionItem[];
        [fields.gateOptions]: OptionItem[];
        [fields.sleeveOptions]: OptionItem[];
        [fields.actuatorOptions]: OptionItem[];
    };
}

const mapDispatchToProps = {
    submitForm,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ThemeProps<BaseTheme>;

interface FlsConfiguratorFormStyles {
    block?: BlockProps;
    container?: GridContainerProps;
    formContainer?: GridContainerProps;
    fieldItem?: GridItemProps;
    fullWidthItem?: GridItemProps;
    actionItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    introText?: TypographyPresentationProps;
    questionnaireText?: TypographyPresentationProps;
    identifierField?: TextFieldPresentationProps;
    selectField?: SelectPresentationProps;
    commentField?: TextAreaPresentationProps;
    clearButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
    successInfosBox?: InfosBoxProps;
    successModal?: ModalPresentationProps;
    continueButton?: ButtonPresentationProps;
    errorInfosBox?: InfosBoxProps;
    loadingOverlay?: LoadingOverlayProps;
    formActions?: FormActionsProps;
}

export const flsConfiguratorFormStyles: FlsConfiguratorFormStyles = {
    block: configurationFormProps.block,
    container: configurationFormProps.container,
    formContainer: configurationFormProps.formContainer,
    fieldItem: configurationFormProps.fieldItem,
    fullWidthItem: configurationFormProps.fullWidthItem,
    actionItem: configurationFormProps.actionItem,
    titleText: configurationFormProps.titleText,
    introText: configurationFormProps.introText,
    questionnaireText: configurationFormProps.questionnaireText,
    clearButton: configurationFormProps.clearButton,
    submitButton: configurationFormProps.submitButton,
    successInfosBox: configurationFormProps.successInfosBox,
    successModal: configurationFormProps.successModal,
    continueButton: configurationFormProps.continueButton,
    errorInfosBox: configurationFormProps.errorInfosBox,
    loadingOverlay: configurationFormProps.loadingOverlay,
    formActions: configurationFormProps.formActions,
};

const Spacer = styled.div`
    width: 100%;
    margin-bottom: ${pxToRem(12)};
    border-bottom: 1px solid ${getColor("common.border")};
`;

type FormState = CustomerFormState & {
    identifier: string;
    series: string;
    size: string;
    flange: string;
    gate: string;
    sleeve: string;
    actuator: string;
    comment: string;
};

type FormErrors = Partial<Record<keyof FormState, string>>;

const initialState: FormState = {
    ...emptyCustomerFormState(),
    identifier: "",
    series: "",
    size: "",
    flange: "",
    gate: "",
    sleeve: "",
    actuator: "",
    comment: "",
};

const toFormFields = (form: FormState): FormFieldModel[] => {
    return [
        { name: "FLS or Competitor Product Identifier", value: form.identifier },
        { name: "Series", value: form.series },
        { name: "Size", value: form.size },
        { name: "Flange Style", value: form.flange },
        { name: "Gate Material", value: form.gate },
        { name: "Sleeve Material", value: form.sleeve },
        { name: "Actuator Type", value: form.actuator },
        { name: "Question or comment", value: form.comment, format: "long" },
    ];
};

const styles = flsConfiguratorFormStyles;

const overlayStyles = mergeProps(styles.loadingOverlay, {
    css: css`
        position: relative;
    `,
});

const mapCmsOptions = (items?: OptionItem[]) =>
    (items ?? [])
        .map(({ fields: { value, label } }) => ({
            value,
            label: label || value,
        }))
        .filter(x => x.value);

const makeOptionsField = (name: string, displayName: string, defaultValue: OptionItem[]) =>
    ({
        name,
        displayName,
        editorTemplate: "ListField",
        fieldType: "Contextual",
        defaultValue,

        getDisplay: (item: OptionItem) => item.fields?.label || item.fields?.value || "",

        fieldDefinitions: [
            {
                name: optionFields.value,
                displayName: "value",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
            } as const,
            {
                name: optionFields.label,
                displayName: "label",
                editorTemplate: "TextField",
                defaultValue: "",
            } as const,
        ],
    }) as any;

const FlsValveConfiguratorForm = ({ fields, submitForm }: Props) => {
    const [form, setForm] = useState<FormState>(initialState);
    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | undefined>(undefined);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

    const [customerErrors, setCustomerErrors] = useState<CustomerFormErrors>({});
    const [reloadCustomerInformationFields, setReloadCustomerInformationFields] = useState(true);

    const seriesOptions = useMemo(() => mapCmsOptions(fields.seriesOptions), [fields.seriesOptions]);
    const sizeOptions = useMemo(() => mapCmsOptions(fields.sizeOptions), [fields.sizeOptions]);
    const flangeOptions = useMemo(() => mapCmsOptions(fields.flangeOptions), [fields.flangeOptions]);
    const gateOptions = useMemo(() => mapCmsOptions(fields.gateOptions), [fields.gateOptions]);
    const sleeveOptions = useMemo(() => mapCmsOptions(fields.sleeveOptions), [fields.sleeveOptions]);
    const actuatorOptions = useMemo(() => mapCmsOptions(fields.actuatorOptions), [fields.actuatorOptions]);

    const maxCommentLength = 2000;

    const handleChange =
        (fieldName: keyof FormState) =>
        (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
            const rawValue = event.target.value;
            const value =
                fieldName === "comment" && rawValue.length > maxCommentLength
                    ? rawValue.slice(0, maxCommentLength)
                    : rawValue;

            setForm(prev => ({ ...prev, [fieldName]: value }));

            if (errors[fieldName]) {
                setErrors(prev => ({ ...prev, [fieldName]: undefined }));
            }

            setReloadCustomerInformationFields(false);
        };

    const validate = (): boolean => {
        const newErrors: FormErrors = { ...customerErrors };

        if (!form.series) {
            newErrors.series = translate("FlsValveConfigurator.Series.Required");
        }

        if (!form.size) {
            newErrors.size = translate("FlsValveConfigurator.Size.Required");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleClear = () => {
        setForm(initialState);
        setErrors({});
        setSubmitError(undefined);
        setIsSuccessModalOpen(false);
        setReloadCustomerInformationFields(true);
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();

        setSubmitError(undefined);

        if (!validate()) {
            return;
        }

        setIsSubmitting(true);

        submitForm({
            formType: "ValveConfigurator",
            formFields: toFormFields(form),
            customerInformation: toCustomerInformationModel(form),
            onSuccess: onSubmitFormSuccess,
            onError: onSubmitFormError,
            onComplete: onSubmitFormComplete,
        });
    };

    const onSubmitFormSuccess = () => {
        setIsSuccessModalOpen(true);
    };

    const onSubmitFormComplete = () => {
        setIsSubmitting(false);
    };

    const onSubmitFormError = (error: string) => {
        setSubmitError(error);
    };

    const closeSuccessModal = () => {
        setIsSuccessModalOpen(false);
        handleClear();
    };

    const title = fields.title || translate("Configuration form");
    const intro =
        fields.introText ||
        translate(
            "If you are not sure which options to select, just complete the required fields and finalize the configuration with an FLS representative.",
        );
    const questionnaireText =
        fields.questionnaireText ||
        translate("To view or download a complete version of the valve questionnaire, please");

    const questionnaireUrl = fields.questionnaireUrl || "#";

    return (
        <Block {...styles.block}>
            <FormWrapper generalErrorMessage={submitError}>
                <LoadingOverlay {...overlayStyles} loading={isSubmitting}>
                    <GridContainer {...styles.formContainer} as="form" onSubmit={handleSubmit}>
                        <GridItem {...styles.fullWidthItem}>
                            <Typography {...styles.titleText}>{title}</Typography>
                        </GridItem>
                        <GridItem {...styles.fullWidthItem}>
                            <Spacer />
                        </GridItem>
                        <GridItem {...styles.fullWidthItem}>
                            <Typography {...styles.introText}>{intro}</Typography>
                        </GridItem>
                        <CustomerInformationFields
                            {...{
                                form,
                                updateCustomerState: customerState => {
                                    setForm(prev => ({ ...prev, ...customerState }));
                                },
                                onHandleChange: handleChange,
                                onValidationChange: (
                                    customerErrors: Partial<Record<keyof CustomerFormState, string>>,
                                ) => {
                                    setCustomerErrors(() => ({ ...customerErrors }));
                                },
                                errors,
                                reloadCustomerInformationFields,
                            }}
                        />
                        <GridItem {...styles.fullWidthItem}>
                            <TextField
                                {...styles.identifierField}
                                label={translate("FlsValveConfigurator.Identifier.Label")}
                                placeholder={translate("FlsValveConfigurator.Identifier.Placeholder")}
                                value={form.identifier}
                                onChange={handleChange("identifier")}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldItem}>
                            <Select
                                {...styles.selectField}
                                label={`${translate("FlsValveConfigurator.Series.Label")}*`}
                                placeholder={translate("FlsValveConfigurator.Series.Placeholder")}
                                value={form.series}
                                onChange={handleChange("series")}
                                error={errors.series}
                            >
                                <option value="">{translate("FlsValveConfigurator.Series.Placeholder")}</option>
                                {seriesOptions.map(o => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.fieldItem}>
                            <Select
                                {...styles.selectField}
                                label={`${translate("FlsValveConfigurator.Size.Label")}*`}
                                placeholder={translate("FlsValveConfigurator.Size.Placeholder")}
                                value={form.size}
                                onChange={handleChange("size")}
                                error={errors.size}
                            >
                                <option value="">{translate("FlsValveConfigurator.Size.Placeholder")}</option>
                                {sizeOptions.map(o => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.fieldItem}>
                            <Select
                                {...styles.selectField}
                                label={translate("FlsValveConfigurator.Flange.Label")}
                                placeholder={translate("FlsValveConfigurator.Flange.Placeholder")}
                                value={form.flange}
                                onChange={handleChange("flange")}
                            >
                                <option value="">{translate("FlsValveConfigurator.Flange.Placeholder")}</option>
                                {flangeOptions.map(o => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.fieldItem}>
                            <Select
                                {...styles.selectField}
                                label={translate("FlsValveConfigurator.Gate.Label")}
                                placeholder={translate("FlsValveConfigurator.Gate.Placeholder")}
                                value={form.gate}
                                onChange={handleChange("gate")}
                            >
                                <option value="">{translate("FlsValveConfigurator.Gate.Placeholder")}</option>
                                {gateOptions.map(o => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.fieldItem}>
                            <Select
                                {...styles.selectField}
                                label={translate("FlsValveConfigurator.Sleeve.Label")}
                                placeholder={translate("FlsValveConfigurator.Sleeve.Placeholder")}
                                value={form.sleeve}
                                onChange={handleChange("sleeve")}
                            >
                                <option value="">{translate("FlsValveConfigurator.Sleeve.Placeholder")}</option>
                                {sleeveOptions.map(o => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.fieldItem}>
                            <Select
                                {...styles.selectField}
                                label={translate("FlsValveConfigurator.Actuator.Label")}
                                placeholder={translate("FlsValveConfigurator.Actuator.Placeholder")}
                                value={form.actuator}
                                onChange={handleChange("actuator")}
                            >
                                <option value="">{translate("FlsValveConfigurator.Actuator.Placeholder")}</option>
                                {actuatorOptions.map(o => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.fullWidthItem}>
                            <TextArea
                                {...styles.commentField}
                                label={translate("Question or comment")}
                                placeholder={translate("Placeholder")}
                                value={form.comment}
                                onChange={handleChange("comment")}
                                rows={4}
                                hint={`${maxCommentLength - form.comment.length} ${translate("characters left")}`}
                            />
                        </GridItem>
                        <GridItem {...styles.fullWidthItem}>
                            <Typography {...styles.questionnaireText}>
                                {questionnaireText}{" "}
                                <Link href={questionnaireUrl} target="_blank" rel="noopener noreferrer">
                                    {translate("click here")}
                                </Link>
                                {"."}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.fullWidthItem}>
                            <Spacer />
                        </GridItem>
                        <GridItem {...styles.actionItem}>
                            <FormActions {...styles.formActions}>
                                <Button
                                    {...styles.clearButton}
                                    type="button"
                                    onClick={handleClear}
                                    data-test-selector="flsConfigurator_clear"
                                >
                                    {translate("Clear")}
                                </Button>
                                <Button
                                    {...styles.submitButton}
                                    type="submit"
                                    disabled={isSubmitting || isSuccessModalOpen}
                                    data-test-selector="flsConfigurator_requestQuote"
                                >
                                    {isSubmitting ? translate("Submitting...") : translate("Request a quote")}
                                </Button>
                            </FormActions>
                        </GridItem>
                    </GridContainer>
                    <Modal
                        headline={translate("Request sent")}
                        contentLabel={translate("Your configuration has been submitted.")}
                        {...styles.successModal}
                        isOpen={isSuccessModalOpen}
                        handleClose={closeSuccessModal}
                    >
                        <InfosBox {...styles.successInfosBox}>
                            {translate(
                                "Your configuration has been submitted. A representative will contact you shortly.",
                            )}
                        </InfosBox>
                        <FormActions>
                            <Button
                                {...styles.continueButton}
                                onClick={closeSuccessModal}
                                data-test-selector="flsConfigurator_continue"
                            >
                                {translate("Continue")}
                            </Button>
                        </FormActions>
                    </Modal>
                </LoadingOverlay>
            </FormWrapper>
        </Block>
    );
};

const widgetModule: WidgetModule = {
    component: connect(() => ({}), mapDispatchToProps)(withTheme(FlsValveConfiguratorForm)),
    definition: {
        group: "Products",
        icon: "form",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Configuration form",
                fieldType: "Contextual",
            },
            {
                name: fields.introText,
                displayName: "Intro text",
                editorTemplate: "TextField",
                defaultValue:
                    "If you are not sure which options to select, just complete the required fields and finalize the configuration with an FLS representative.",
                fieldType: "Contextual",
            },
            {
                name: fields.questionnaireText,
                displayName: "Questionnaire text",
                editorTemplate: "TextField",
                defaultValue: "To view or download a complete version of the valve questionnaire, please",
                fieldType: "Contextual",
            },
            {
                name: fields.questionnaireUrl,
                displayName: "Questionnaire URL",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            makeOptionsField(fields.seriesOptions, "Valve series options", [
                { fields: { value: "TG", label: "TG" } },
                { fields: { value: "TGW", label: "TGW" } },
            ]),
            makeOptionsField(fields.sizeOptions, "Valve size options", [
                { fields: { value: "2", label: "2" } },
                { fields: { value: "3", label: "3" } },
                { fields: { value: "4", label: "4" } },
                { fields: { value: "6", label: "6" } },
                { fields: { value: "8", label: "8" } },
                { fields: { value: "10", label: "10" } },
                { fields: { value: "12", label: "12" } },
                { fields: { value: "14", label: "14" } },
                { fields: { value: "16", label: "16" } },
                { fields: { value: "20", label: "20" } },
                { fields: { value: "24", label: "24" } },
                { fields: { value: "26", label: "26" } },
                { fields: { value: "28", label: "28" } },
                { fields: { value: "30", label: "30" } },
                { fields: { value: "36", label: "36" } },
                { fields: { value: "40", label: "40" } },
                { fields: { value: "42", label: "42" } },
                { fields: { value: "48", label: "48" } },
            ]),
            makeOptionsField(fields.flangeOptions, "Flange style options", [
                { fields: { value: "ANSI 150", label: "ANSI 150" } },
                { fields: { value: "ANSI 150 w/metric hardware", label: "ANSI 150 w/metric hardware" } },
                { fields: { value: "DIN PN10", label: "DIN PN10" } },
                { fields: { value: "AS Table D", label: "AS Table D" } },
                { fields: { value: "AS Table E", label: "AS Table E" } },
                { fields: { value: "AS Table D&E", label: "AS Table D&E" } },
                { fields: { value: "DIN PN16", label: "DIN PN16" } },
            ]),
            makeOptionsField(fields.gateOptions, "Gate material options", [
                { fields: { value: "316L", label: "316L" } },
                { fields: { value: "17-4 PH", label: "17-4 PH" } },
                { fields: { value: "Hastelloy C22", label: "Hastelloy C22" } },
                { fields: { value: "Hastelloy C276", label: "Hastelloy C276" } },
                { fields: { value: "Duplex 2205", label: "Duplex 2205" } },
                { fields: { value: "Duplex 2507", label: "Duplex 2507" } },
                { fields: { value: "A20", label: "A20" } },
            ]),
            makeOptionsField(fields.sleeveOptions, "Sleeve material options", [
                { fields: { value: "Pure Gum", label: "Pure Gum" } },
                { fields: { value: "Chlorobutyl", label: "Chlorobutyl" } },
                { fields: { value: "Neoprene", label: "Neoprene" } },
                { fields: { value: "EPDM", label: "EPDM" } },
                { fields: { value: "Nitrele", label: "Nitrele" } },
                { fields: { value: "UMP 312 Polyurethane", label: "UMP 312 Polyurethane" } },
            ]),
            makeOptionsField(fields.actuatorOptions, "Actuator type options", [
                { fields: { value: "Standard Air Cylinder (80 PSI)", label: "Standard Air Cylinder (80 PSI)" } },
                {
                    fields: {
                        value: "Special Air Cylinder (60PSI Supply)",
                        label: "Special Air Cylinder (60PSI Supply)",
                    },
                },
                {
                    fields: {
                        value: "Air Cylinder w/ Spring Return Open",
                        label: "Air Cylinder w/ Spring Return Open",
                    },
                },
                {
                    fields: {
                        value: "Air Cylinder w/ Spring Return Closed",
                        label: "Air Cylinder w/ Spring Return Closed",
                    },
                },
                {
                    fields: {
                        value: "ACMO w/ Size <= 10 (Handwheel Override)",
                        label: "ACMO w/ Size <= 10 (Handwheel Override)",
                    },
                },
                { fields: { value: "Bevel Gear", label: "Bevel Gear" } },
                { fields: { value: "Electro-Mechanical Limitorque", label: "Electro-Mechanical Limitorque" } },
                { fields: { value: "ELECRT", label: "ELECRT" } },
                { fields: { value: "ELECAU", label: "ELECAU" } },
                { fields: { value: "Hydraulic Cylinder (1800 PSI)", label: "Hydraulic Cylinder (1800 PSI)" } },
                { fields: { value: "Hydraulic Cylinder (3000 PSI)", label: "Hydraulic Cylinder (3000 PSI)" } },
                { fields: { value: "Handwheel", label: "Handwheel" } },
                { fields: { value: "Reduced HW", label: "Reduced HW" } },
                { fields: { value: "Chainwheel", label: "Chainwheel" } },
            ]),
        ],
    },
};

export default widgetModule;
